import { useState } from "react";
import { useUi } from "@/state/ui";
import { useWarnings, useDistinctNotes, useDistinctWarnings } from "@/state/warnings";
import { foldedSummary } from "./warningsSummary";

/** Warnings shown before "… N more". */
const SHOWN = 4;
/** Characters past which a warning likely runs off one line (#86's pool lists). */
const LONG = 160;

/**
 * meta.warnings of recent responses, deduplicated, and behind a quiet toggle
 * their meta.notes (how the figures were made, not problems). On a phone
 * (`narrow`) the bar folds to one line with the counts (#129); opening it
 * shows every warning in full.
 *
 * On a touch screen the "… N more" and notes toggles sit one line apart, so
 * each gets about 30 px of height: the shared 32 px hit area (index.css)
 * of the lower one would otherwise cover the upper one.
 */
export function WarningsBar() {
  const warnings = useDistinctWarnings();
  const notes = useDistinctNotes();
  const clear = useWarnings((s) => s.clear);
  const narrow = useUi((s) => s.narrow);
  return <WarningsBarView warnings={warnings} notes={notes} narrow={narrow} onDismiss={() => clear()} />;
}

/** The bar itself, given what to show (WarningsBar wires it to the stores). */
export function WarningsBarView({
  warnings,
  notes,
  narrow,
  onDismiss,
}: {
  warnings: string[];
  notes: string[];
  narrow: boolean;
  onDismiss: () => void;
}) {
  const [allWarnings, setAllWarnings] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [unfolded, setUnfolded] = useState(false);
  if (warnings.length === 0 && notes.length === 0) return null;
  const tone =
    warnings.length > 0
      ? "border-b border-warn/40 bg-warn-bg text-warn"
      : "border-b border-border text-fg-faint";
  const dismiss = (
    <button
      type="button"
      className="text-fg-faint hover:text-fg"
      onClick={onDismiss}
      aria-label="dismiss warnings and notes"
    >
      ×
    </button>
  );
  if (narrow && !unfolded)
    return (
      <div className={`flex min-h-8 shrink-0 items-center gap-2 px-2 text-xs ${tone}`} role="status">
        <button
          type="button"
          className="flex min-h-8 min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={false}
          onClick={() => setUnfolded(true)}
        >
          <span className="truncate">{foldedSummary(warnings.length, notes.length)}</span>
          <span className="ml-auto shrink-0 underline decoration-dotted">show</span>
        </button>
        {dismiss}
      </div>
    );
  // unfolded on a phone: every warning, in full
  const full = allWarnings || narrow;
  const shown = full ? warnings : warnings.slice(0, SHOWN);
  const hidden = warnings.length - shown.length;
  // a long warning is cut to one line until the bar is opened in full
  const long = warnings.slice(0, SHOWN).some((w) => w.length > LONG);
  return (
    <div
      className={
        warnings.length > 0
          ? "flex shrink-0 items-start gap-2 border-b border-warn/40 bg-warn-bg px-2 py-0.5 text-2xs text-warn"
          : "flex shrink-0 items-start gap-2 border-b border-border px-2 py-0.5 text-2xs text-fg-faint"
      }
      role="status"
    >
      <div className="min-w-0 flex-1">
        {narrow && (
          <button
            type="button"
            className="underline decoration-dotted hover:text-fg"
            aria-expanded
            onClick={() => setUnfolded(false)}
          >
            hide
          </button>
        )}
        {shown.map((w) => (
          <div key={w} className={full ? "break-words" : "truncate"} title={full ? undefined : w}>
            ⚠ {w}
          </div>
        ))}
        {!narrow && (warnings.length > SHOWN || long) && (
          <button
            type="button"
            className="underline decoration-dotted hover:text-fg pointer-coarse:py-2"
            aria-expanded={allWarnings}
            onClick={() => setAllWarnings((v) => !v)}
          >
            {allWarnings ? "show fewer" : hidden > 0 ? `… ${hidden} more` : "show in full"}
          </button>
        )}
        {notes.length > 0 && (
          <div className="text-fg-faint">
            <button
              type="button"
              className="underline decoration-dotted hover:text-fg pointer-coarse:py-2"
              aria-expanded={showNotes}
              onClick={() => setShowNotes((v) => !v)}
            >
              {showNotes
                ? "hide notes"
                : `${notes.length} note${notes.length === 1 ? "" : "s"} on how these figures are made`}
            </button>
            {showNotes && (
              <ul className="mt-0.5 space-y-0.5">
                {notes.map((n) => (
                  <li key={n} className="break-words">
                    ⓘ {n}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {dismiss}
    </div>
  );
}
